import {
  useEffect, useMemo, useRef, useState,
} from "react";
import { useInfiniteQuery, useQuery } from "react-query";
import { useSnackbar } from "@kitbird/core";
import { ContentType } from "./constants";
import {
  getTrxRedReceipt,
  getTrxRedReceiptHistory,
  GetTrxRedReceiptParamsI,
  getTrxWhiteReceiptByRedReceipt,
  getTrxWhiteReceiptDetail,
  IGetTrxWhiteReceiptData,
  postCloseReceipt,
  postExtendReceipt,
  postVerifyTransfer,
} from "../../../services/trxRedReceiptService";
import useAuth from "../../../components/Authentication/useAuth";
import { byteToString, dateToFormatedStringDate } from "../../../utils/common";
import { UploadMediaI } from "../../../typings/common";
import { useDebounce } from "../../../hooks";
import { getDocUrl } from "../../../services/bbOneGcsService";
import { getPoolsByArea } from "../../../services/poolService";
import { getAreas } from "../../../services/areaService";
import { getBusinessSegment } from "../../../services/businessService/businessService";
import { getSegments } from "../../../services/segmentService";
import getHasAccessAction from "../../../components/Authentication/utils";

interface FilterI {
  komparasi?: string[],
  search?: string,
  area?: {
    label: string,
    value: string,
  },
  pool?: {
    label: string,
    value: string,
  },
  segment?: {
    label: string,
    value: string,
  },
  receiptType?: {
    label: string,
    value: string,
  },
  receiptExpiry?: {
    label: string,
    value: string,
  },
  dates: Date[],
  page_number: number,
  page_size: number,
  sortBy: string,
  sortType: "ASC" | "DSC" | "DEFAULT",
}

const initErrors = {
  uploadedMedia: "",
  transferDate: "",
};

const useCloseReceipt = (pageName: "Close-bon-putih-ijiran" | "Verifikasi-bon") => {
  const { rbacInfo, userAccount, userInfo } = useAuth();
  const snackbar = useSnackbar();
  const obsRef = useRef(null);
  const tableRef = useRef(document.createElement("table"));
  const setTimeoutRef = useRef(setTimeout(() => null));
  const [isLoading, setLoading] = useState(false);
  const [areaOpts, setAreaOpts] = useState<{ label: string, value: string }[]>([]);
  const [poolOpts, setPoolOpts] = useState<{ label: string, value: string }[]>([]);
  const [segmentOpts, setSegmentOpts] = useState<{ label: string, value: string }[]>([]);
  const [params, setParams] = useState<FilterI>({
    komparasi: ["Pas", "Kurang", "Lebih"],
    area: areaOpts.length === 1
      ? areaOpts[0]
      : undefined,
    pool: poolOpts.length === 1
      ? poolOpts[0]
      : undefined,
    segment: segmentOpts.length === 1
      ? segmentOpts[0]
      : undefined,
    receiptExpiry: { label: "Lihat semua", value: "" },
    receiptType: { label: "Lihat semua", value: "" },
    dates: [new Date(), new Date()],
    page_number: 1,
    page_size: 10,
    sortBy: "",
    sortType: "DSC",
  });

  const [meta, setMeta] = useState({
    page: 1,
    total_items: 0,
    total_pages: 0,
  });

  const [selectedRows, setSelectedRows] = useState<GetTrxRedReceiptParamsI[]>([]);
  const [contentType, setContentType] = useState<ContentType>(ContentType.NOT_SELECTED);
  const [selectedWhiteReceipt, setSelectedWhiteReceipt] = useState<IGetTrxWhiteReceiptData>();
  const [detailRedReceipt, setDetailRedReceipt] = useState<GetTrxRedReceiptParamsI>();
  const [isExtendConfirmModal, setExtendConfirmModal] = useState(false);
  const [isSuccessExtendConfirm, setSuccessExtendConfirm] = useState(false);
  const [isVerifyTransferModal, setVerifyTransferModal] = useState(false);
  const [isCloseConfirm, setCloseConfirm] = useState(false);

  const [uploadedMedia, setUploadedMedia] = useState<UploadMediaI>();
  const [transferDate, setTransferDate] = useState<Date>();
  const [searchText, setSearchText] = useState("");
  const [isViewFile, setViewFile] = useState(false);
  const [docUrl, setDocUrl] = useState("");
  const [errorMessages, setErrorMessages] = useState(initErrors);

  useEffect(() => {
    setParams((curr) => ({ ...curr, search: searchText }));
  }, [useDebounce(searchText, 300)]);

  useEffect(() => {
    if (!isVerifyTransferModal) {
      setUploadedMedia(undefined);
      setTransferDate(undefined);
      setErrorMessages(initErrors);
    }
  }, [isVerifyTransferModal]);

  const { data: dataWhiteReceipt, refetch: refetchWhiteReceipt } = useQuery({
    queryKey: [selectedRows],
    enabled: !!selectedRows.length,
    queryFn: () => getTrxWhiteReceiptByRedReceipt(selectedRows[0].trx_id_bm),
  });

  const { data: dataWhiteReceiptDetail } = useQuery({
    queryKey: [selectedWhiteReceipt?.trx_id],
    enabled: !!selectedWhiteReceipt,
    queryFn: ({ queryKey }) => (queryKey[0]
      ? getTrxWhiteReceiptDetail(queryKey[0])
      : undefined
    ),
  });

  useEffect(() => {
    if (dataWhiteReceipt) {
      setContentType(ContentType.SELECTED);
    }
  }, [dataWhiteReceipt, params]);

  useEffect(() => {
    if (
      contentType !== ContentType.SUCCESS
    ) clearTimeout(setTimeoutRef.current);
  }, [contentType]);

  const { data: dataAllSegments, isLoading: isAllSegmentsLoading } = useQuery({
    queryKey: ["get_all_segments"],
    queryFn: getSegments,
  });
  const { data: dataAreas, isLoading: isAreasLoading } = useQuery({
    queryKey: ["get_areas"],
    queryFn: getAreas,
  });

  const couldFilterAllCloseReceipt = getHasAccessAction({
    menus: rbacInfo,
    menuTitle: "Flow enable all filter close bon",
  });

  useEffect(() => {
    if (!isAreasLoading) {
      if (
        dataAreas
        && Array.isArray(dataAreas?.listareaall)
        && (
          pageName === "Verifikasi-bon"
          || couldFilterAllCloseReceipt
        )
      ) {
        setAreaOpts(dataAreas.listareaall.map((area) => ({ label: area.name, value: area.id })));
      } else if (
        userAccount?.position_info
        && pageName === "Close-bon-putih-ijiran"
      ) {
        setAreaOpts(Array.from(new Set(userAccount.position_info.map((item) => (
          JSON.stringify({ label: item.area_name.replace(/[^a-zA-Z\s]/g, ""), value: item.area_id })
        )))).map((item) => JSON.parse(item)));
      } else setAreaOpts([]);
    }
  }, [dataAreas, pageName]);

  const { data: dataPools, isLoading: isPoolsLoading } = useQuery({
    queryKey: ["get_pools", params.area],
    enabled: !!params.area,
    queryFn: () => getPoolsByArea(params.area?.value as string),
  });

  useEffect(() => {
    if (!isPoolsLoading && params.area?.value) {
      if (
        dataPools
        && Array.isArray(dataPools?.list_pool)
        && (
          pageName === "Verifikasi-bon"
          || couldFilterAllCloseReceipt
        )
      ) {
        setPoolOpts(dataPools.list_pool.map((pool) => (
          { label: pool.pool_name, value: pool.pool_id }
        )));
      } else if (
        userAccount?.userPools
        && pageName === "Close-bon-putih-ijiran"
      ) {
        setPoolOpts(userAccount.userPools.map((item) => (
          { label: item.poolName, value: item.poolId }
        )));
      } else setPoolOpts([]);
    }
  }, [dataPools, pageName]);

  const { data: dataSegments, isLoading: isSegmentsLoading } = useQuery({
    queryKey: ["get_segments", params.area, params.pool],
    enabled: !!params.area && !!params.pool,
    queryFn: () => getBusinessSegment("P", params.area?.value as string, params.pool?.value as string),
  });

  useEffect(() => {
    if (!isSegmentsLoading && params.pool?.value) {
      if (
        dataSegments
        && Array.isArray(dataSegments?.lstmstbusiness)
        && (
          pageName === "Verifikasi-bon"
          || couldFilterAllCloseReceipt
        )
      ) {
        const codeSegments = dataSegments.lstmstbusiness
          .map((item) => (item.code_segment));
        const opts = dataAllSegments?.listSegment
          .filter((item) => codeSegments.includes(item.code_segment))
          .map((item) => ({ label: item.code_description, value: item.code_segment }))
          || [];
        setSegmentOpts(opts);
      } else if (
        userAccount?.position_info
        && pageName === "Close-bon-putih-ijiran"
      ) {
        setSegmentOpts(Array.from(new Set(userAccount.position_info.map((item) => (
          JSON.stringify({
            label: item.segments_info.desc_segment,
            value: item.segments_info.code_segment,
          })
        )))).map((item) => JSON.parse(item)));
      } else setSegmentOpts([]);
    }
  }, [dataSegments, pageName]);

  const fetchList = async (pageParam: number) => {
    const startDate = params.dates && params.dates[0];
    const endDate = params.dates && params.dates[1];
    const formatedStartDate = dateToFormatedStringDate(startDate);
    const formatedEndDate = dateToFormatedStringDate(endDate);
    const sortBy = params.sortType === "DEFAULT" ? "" : params.sortBy;
    const sortType = params.sortType !== "ASC" ? "DESC" : "ASC";

    const hasRequiredParams = !!(params.area && params.pool);
    if (pageName === "Verifikasi-bon") {
      return hasRequiredParams
        ? getTrxRedReceiptHistory({
          search: params.search,
          area_id: params.area?.value,
          pool_id: params.pool?.value,
          segment: params.segment?.value,
          start_date: formatedStartDate,
          end_date: formatedEndDate,
          sort_by: sortBy,
          sort_type: sortType,
          page_number: pageParam,
          page_size: params.page_size,
        }).then((res) => {
          if (res.metadata) setMeta(res.metadata);
          return res.list_trx_closebon_history;
        }).catch(() => [])
          .finally(() => {
            if (isSuccessExtendConfirm) setSuccessExtendConfirm(false);
            else {
              setSelectedRows([]);
              setContentType((curr) => (
                curr !== ContentType.SUCCESS ? ContentType.NOT_SELECTED : curr
              ));
            }
          })
        : [];
    }
    return hasRequiredParams
      ? getTrxRedReceipt({
        komparasi: params.komparasi?.join(","),
        search: params.search,
        area_id: params.area?.value,
        pool_id: params.pool?.value,
        segment: params.segment?.value,
        status_trx_bon: params.receiptType?.value,
        status_expired_bon: params.receiptExpiry?.value,
        sort_by: sortBy,
        sort_type: sortType,
        page_number: pageParam,
        page_size: params.page_size,
      }).then((res) => {
        if (res.metadata) setMeta(res.metadata);
        return res.list_trx_bm;
      }).catch(() => [])
        .finally(() => {
          if (isSuccessExtendConfirm) setSuccessExtendConfirm(false);
          else {
            setSelectedRows([]);
            setContentType((curr) => (
              curr !== ContentType.SUCCESS ? ContentType.NOT_SELECTED : curr
            ));
          }
        })
      : [];
  };

  const {
    data: redReceiptPages = { pages: [] },
    isFetching,
    isFetchingNextPage,
    refetch: refetchTable,
    fetchNextPage,
    remove,
    hasNextPage,
  } = useInfiniteQuery(
    ["redReceiptList", params],
    async ({ queryKey, pageParam = 1 }) => (
      fetchList(pageParam)
    ),
    {
      getNextPageParam: () => (
        meta.page < meta.total_pages
          ? meta.page + 1
          : undefined
      ),
      keepPreviousData: true,
    },
  );

  const redReceiptList = useMemo(
    () => redReceiptPages.pages.flatMap((page) => page),
    [redReceiptPages, params],
  );

  const handleCloseReceipt = (trxIdRedReceipt: string) => {
    setLoading(true);

    postCloseReceipt(trxIdRedReceipt)
      .then((res) => {
        if (res.responseCode === "Succeed") {
          setSelectedRows([]);
          refetchTable();
          setContentType(ContentType.SUCCESS);
          setCloseConfirm(false);
          snackbar.show("Bon telah diverifikasi & langsung Close bon", "success");
          setTimeoutRef.current = setTimeout(() => {
            setContentType(ContentType.NOT_SELECTED);
          }, 5000);
        } else snackbar.show(res.responseMessage || "Gagal close bon putih ijiran", "error");
      })
      .catch(() => snackbar.show("Gagal close bon putih ijiran", "error"))
      .finally(() => setLoading(false));
  };

  const handleExtendReceipt = (trxIdRedReceipt: string) => {
    setLoading(true);
    setSuccessExtendConfirm(true);
    const jobTitle = userAccount.position_info.length
      ? userAccount.position_info[0].job_title
      : "";

    postExtendReceipt({
      trx_id: trxIdRedReceipt,
      update_by: userInfo.preferred_username,
      extention_job_title: jobTitle,
    })
      .then((res) => {
        if (res.responseCode === "Succeed") {
          refetchTable();
          refetchWhiteReceipt();
          setExtendConfirmModal(false);
          snackbar.show(res.responseMessage || "Masa aktif bon telah diperpanjang", "success");
        } else snackbar.show(res.responseMessage || "Gagal perpanjang masa aktif bon", "error");
      })
      .catch(() => snackbar.show("Gagal perpanjang masa aktif bon", "error"))
      .finally(() => setLoading(false));
  };

  const processFiles = (files: FileList | null) => {
    if (files && Object.keys(files).length > 0) {
      const newFile = files[0];
      const reader = new FileReader();
      reader.readAsDataURL(newFile);
      reader.onload = (event: any) => {
        const val: UploadMediaI = {
          FileName: newFile.name.toLowerCase(),
          FileSize: byteToString(newFile.size as number, "B", false),
          FileSizeString: byteToString(newFile.size as number),
          FileType: newFile.type.toLowerCase(),
          FileUrl: event.target.result.split(",")[1],
          FileProgessSize: 0,
          FileProgress: 0,
        };

        const totalFileSize = Number.parseFloat(val.FileSize.split(" ")[0]);
        const fileSizeLimit = 1 * 1024 * 1024;
        const isFileSizeAcceptable = fileSizeLimit >= totalFileSize;
        const fileType = val.FileName.split(".").pop() || "";
        const isFileTypeAcceptable = (/\b(png|jpe?g|pdf)\b/i).test(fileType);

        setUploadedMedia(val);
        if (isFileSizeAcceptable && isFileTypeAcceptable) {
          setUploadedMedia(val);
        } else if (!isFileSizeAcceptable && isFileTypeAcceptable) {
          setErrorMessages((curr) => ({ ...curr, uploadedMedia: "Total ukuran file melebihi 1 MB" }));
        } else if (isFileSizeAcceptable && !isFileTypeAcceptable) {
          setErrorMessages((curr) => ({ ...curr, uploadedMedia: "Jenis file tidak sesuai (bukan .png, .jpg, .jpeg, atau .pdf)" }));
        } else if (!isFileSizeAcceptable && !isFileTypeAcceptable) {
          setErrorMessages((curr) => ({ ...curr, uploadedMedia: "Total ukuran file melebihi 1 MB dan jenis file tidak sesuai" }));
        }
      };
    } else setUploadedMedia(undefined);
  };

  const handleSendTransferProof = (trxIdRedReceipt: string) => {
    setLoading(true);
    const transferDateString = dateToFormatedStringDate(transferDate);
    if (uploadedMedia && transferDateString) {
      postVerifyTransfer({
        trx_id: trxIdRedReceipt,
        file_base64: uploadedMedia.FileUrl,
        file_name: uploadedMedia.FileName,
        file_size: uploadedMedia.FileSize,
        transfer_date: transferDateString,
      })
        .then((res) => {
          if (res.responseCode === "Succeed") {
            refetchTable();
            refetchWhiteReceipt();
            setVerifyTransferModal(false);
            snackbar.show("Berhasil mengajukan verifikasi transfer", "success");
          } else snackbar.show(res.responseMessage || "Gagal mengajukan verifikasi transfer", "error");
        })
        .catch(() => snackbar.show("Gagal mengajukan verifikasi transfer", "error"))
        .finally(() => setLoading(false));
    } else snackbar.show("Harap masukan bukti transfer", "error");
  };

  const handleDisplayDoc = async () => {
    setLoading(true);
    if (dataWhiteReceipt && dataWhiteReceipt.detail_history_closebm.file_receipt) {
      const docUrlString = await getDocUrl({
        file_name: dataWhiteReceipt.detail_history_closebm.file_receipt.file_name,
        path_file: dataWhiteReceipt.detail_history_closebm.file_receipt.file_path,
      });
      setDocUrl(docUrlString);
      setViewFile(true);
    } else snackbar.show("Gagal memuat file", "error");
    setLoading(false);
  };

  const downloadImage = (url: string) => {
    if (dataWhiteReceipt?.detail_history_closebm.file_receipt) {
      fetch(url)
        .then((response) => response.blob())
        .then((blob) => {
          const blobUrl = window.URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.download = dataWhiteReceipt?.detail_history_closebm?.file_receipt?.file_name || "";
          a.href = blobUrl;
          document.body.appendChild(a);
          a.click();
          a.remove();
        });
    }
  };

  const handleDownloadDoc = async () => {
    setLoading(true);
    if (dataWhiteReceipt?.detail_history_closebm.file_receipt) {
      const docUrlString = await getDocUrl({
        file_name: dataWhiteReceipt.detail_history_closebm.file_receipt.file_name,
        path_file: dataWhiteReceipt.detail_history_closebm.file_receipt.file_path,
      });
      downloadImage(docUrlString);
    } else snackbar.show("Gagal download bukti transfer", "error");
    setLoading(false);
  };

  return {
    initErrors,
    pageName,
    obsRef,
    tableRef,
    isLoading,
    setLoading,
    params,
    setParams,
    searchText,
    setSearchText,
    meta,
    setMeta,
    selectedRows,
    setSelectedRows,
    selectedWhiteReceipt,
    setSelectedWhiteReceipt,
    isExtendConfirmModal,
    setExtendConfirmModal,
    isVerifyTransferModal,
    setVerifyTransferModal,
    detailRedReceipt,
    setDetailRedReceipt,
    isCloseConfirm,
    setCloseConfirm,
    uploadedMedia,
    setUploadedMedia,
    transferDate,
    setTransferDate,
    isViewFile,
    setViewFile,
    docUrl,
    setDocUrl,
    errorMessages,
    setErrorMessages,

    areaOpts,
    poolOpts,
    segmentOpts,

    fetchNextPage,
    isFetching,
    isFetchingNextPage,

    contentType,
    redReceiptList,
    dataWhiteReceipt,

    dataWhiteReceiptDetail,

    handleCloseReceipt,
    handleExtendReceipt,
    processFiles,
    handleSendTransferProof,
    handleDisplayDoc,
    handleDownloadDoc,
  };
};

export default useCloseReceipt;
